import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Terminal,
  Play,
  CheckCircle,
  Circle,
  Lightbulb,
  Clock,
  Trophy,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Loader2,
  XCircle,
  HelpCircle,
  Zap,
  Server,
  Cloud,
  Box,
  Settings,
  Award,
} from "lucide-react";

const InteractiveLab = () => {
  const { environment: envParam } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  // Lab state
  const [environments, setEnvironments] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(envParam || "docker");
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState("simulation");
  const [externalServices, setExternalServices] = useState([]);
  
  // Session state
  const [session, setSession] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  
  // Terminal state
  const [history, setHistory] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastOutput, setLastOutput] = useState("");
  
  // UI state
  const [showHint, setShowHint] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  
  const terminalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const fetchInitialData = async () => {
    try {
      const [envsRes, modesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/labs/environments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/labs/modes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/labs/external-services`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEnvironments(envsRes.data);
      setModes(modesRes.data.modes);
      setExternalServices(servicesRes.data);
      
      if (envParam) {
        setSelectedEnv(envParam);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const startSession = async () => {
    setStarting(true);
    try {
      const response = await axios.post(
        `${API}/labs/sessions/start`,
        {
          environment: selectedEnv,
          mode: selectedMode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSession(response.data);
      setExercises(response.data.exercises || []);
      setCompletedExercises([]);
      setScore(0);
      setCurrentExercise(0);
      
      // Initialize terminal
      setHistory([
        { type: "system", content: `🚀 DevOps Learn Lab - ${selectedEnv.toUpperCase()}` },
        { type: "system", content: `Mode: ${selectedMode === "simulation" ? "Simulation" : selectedMode === "docker" ? "Docker réel" : "Service externe"}` },
        { type: "system", content: "─".repeat(50) },
        { type: "system", content: "Tapez 'help' pour voir les commandes disponibles" },
      ]);
      
      if (response.data.external_url) {
        toast.success("Session créée! Utilisez le lien externe.");
      } else {
        toast.success("Session démarrée!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du démarrage");
    } finally {
      setStarting(false);
    }
  };

  const executeCommand = async (command) => {
    if (!command.trim() || !session) return;
    
    setHistory(prev => [...prev, { type: "input", content: `$ ${command}` }]);
    setIsProcessing(true);
    
    try {
      const response = await axios.post(
        `${API}/labs/sessions/${session.session_id}/execute`,
        { session_id: session.session_id, command },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { output, exit_code } = response.data;
      setLastOutput(output || "");
      
      setHistory(prev => [...prev, {
        type: exit_code === 0 ? "output" : "error",
        content: output || "(pas de sortie)"
      }]);
    } catch (error) {
      setHistory(prev => [...prev, {
        type: "error",
        content: "Erreur d'exécution"
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const validateCurrentExercise = async () => {
    if (!session || exercises.length === 0) return;
    
    const exercise = exercises[currentExercise];
    if (!exercise) return;
    
    try {
      const response = await axios.post(
        `${API}/labs/sessions/${session.session_id}/validate`,
        {
          session_id: session.session_id,
          exercise_id: exercise.exercise_id,
          command: currentInput || history[history.length - 2]?.content?.replace("$ ", "") || "",
          output: lastOutput
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.valid) {
        toast.success(response.data.message);
        setCompletedExercises(prev => [...prev, exercise.exercise_id]);
        setScore(response.data.total_score || score + exercise.points);
        
        // Move to next exercise
        if (currentExercise < exercises.length - 1) {
          setCurrentExercise(prev => prev + 1);
        }
        
        if (response.data.all_completed) {
          setShowResults(true);
          setResults({
            score: response.data.total_score,
            maxScore: exercises.reduce((sum, ex) => sum + ex.points, 0),
            completed: exercises.length,
            total: exercises.length
          });
        }
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const endSession = async () => {
    if (!session) return;
    
    try {
      const response = await axios.post(
        `${API}/labs/sessions/${session.session_id}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResults(response.data);
      setShowResults(true);
      setShowEndDialog(false);
    } catch (error) {
      toast.error("Erreur lors de la fin de session");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isProcessing) {
      executeCommand(currentInput);
      setCurrentInput("");
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const envIcons = {
    docker: <Box className="w-5 h-5" />,
    kubernetes: <Server className="w-5 h-5" />,
    terraform: <Settings className="w-5 h-5" />,
    aws: <Cloud className="w-5 h-5" />
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentEx = exercises[currentExercise];
  const progress = exercises.length > 0 
    ? (completedExercises.length / exercises.length) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="interactive-lab-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Terminal className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold">Labs Interactifs</h1>
                <p className="text-muted-foreground text-sm">Pratiquez avec des exercices guidés</p>
              </div>
            </div>
          </div>
          
          {session && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Trophy className="w-4 h-4 text-yellow-400" />
                <span className="font-bold">{score} pts</span>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setShowEndDialog(true)}>
                Terminer
              </Button>
            </div>
          )}
        </div>

        {!session ? (
          /* Lab Selection Screen */
          <div className="space-y-8">
            {/* Environment Selection */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Choisir un environnement
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {environments.map((env) => (
                  <div
                    key={env.id}
                    onClick={() => setSelectedEnv(env.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedEnv === env.id 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedEnv === env.id ? "bg-primary/20" : "bg-muted"
                      }`}>
                        {envIcons[env.id]}
                      </div>
                      <div>
                        <p className="font-bold">{env.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {env.total_exercises} exercices
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{env.description}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {env.total_points} pts
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mode Selection */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-primary" />
                Choisir un mode d'exécution
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                {modes.map((mode) => (
                  <div
                    key={mode.id}
                    onClick={() => setSelectedMode(mode.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedMode === mode.id 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-bold">{mode.name}</p>
                      {selectedMode === mode.id && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{mode.description}</p>
                    <div className="space-y-1">
                      {mode.features.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* External Services (if external mode) */}
            {selectedMode === "external" && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-primary" />
                  Services Externes Disponibles
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {externalServices.map((service) => (
                    <div key={service.id} className="p-4 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-bold">{service.name}</p>
                        {service.free_tier && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">Gratuit</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
                      {service.scenarios[selectedEnv] && (
                        <a 
                          href={service.scenarios[selectedEnv]} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary text-sm flex items-center gap-1 hover:underline"
                        >
                          Ouvrir <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Start Button */}
            <div className="flex justify-center">
              <Button 
                size="lg" 
                onClick={startSession} 
                disabled={starting}
                className="glow-green px-8"
                data-testid="start-lab-btn"
              >
                {starting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Démarrage...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5 mr-2" />
                    Démarrer le Lab {selectedEnv.toUpperCase()}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Active Lab Session */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Exercises Panel */}
            <div className="lg:col-span-1 space-y-4">
              {/* Progress */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Progression</span>
                  <span className="text-sm text-muted-foreground">
                    {completedExercises.length}/{exercises.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Current Exercise */}
              {currentEx && (
                <div className="bg-card border border-primary/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-primary/20 text-primary">
                      Exercice {currentExercise + 1}
                    </Badge>
                    <Badge variant="outline">{currentEx.points} pts</Badge>
                  </div>
                  
                  <h3 className="font-bold text-lg mb-2">{currentEx.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{currentEx.description}</p>
                  
                  <div className="bg-muted/30 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium mb-1">📝 Instructions:</p>
                    <p className="text-sm">{currentEx.instructions}</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowHint(!showHint)}
                    >
                      <Lightbulb className="w-4 h-4 mr-1" />
                      Indice
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={validateCurrentExercise}
                      className="flex-1"
                      data-testid="validate-exercise-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Valider
                    </Button>
                  </div>
                  
                  {showHint && currentEx.hint && (
                    <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400">
                        💡 <code className="bg-muted px-1 rounded">{currentEx.hint}</code>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Exercise List */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h4 className="font-bold mb-3">Tous les exercices</h4>
                <div className="space-y-2">
                  {exercises.map((ex, index) => (
                    <div
                      key={ex.exercise_id}
                      onClick={() => setCurrentExercise(index)}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        currentExercise === index 
                          ? "bg-primary/20" 
                          : "hover:bg-muted/50"
                      }`}
                    >
                      {completedExercises.includes(ex.exercise_id) ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : currentExercise === index ? (
                        <Circle className="w-5 h-5 text-primary" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{ex.title}</p>
                        <p className="text-xs text-muted-foreground">{ex.points} pts</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Terminal Panel */}
            <div className="lg:col-span-2">
              <div 
                className="terminal rounded-lg overflow-hidden h-[600px] flex flex-col"
                onClick={focusInput}
                data-testid="lab-terminal"
              >
                {/* Terminal Header */}
                <div className="bg-[#1a1a1a] px-4 py-2 flex items-center gap-2 border-b border-[#333] shrink-0">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#27ca3f]"></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2 font-mono">
                    {session.mode === "docker" ? "🐳 " : session.mode === "external" ? "☁️ " : "💻 "}
                    devops@learn:~/{selectedEnv}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {session.mode === "simulation" ? "Simulation" : session.mode === "docker" ? "Docker" : "External"}
                    </Badge>
                  </div>
                </div>

                {/* External URL Notice */}
                {session.external_url && (
                  <div className="bg-blue-500/10 border-b border-blue-500/30 px-4 py-2">
                    <a 
                      href={session.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-400 flex items-center gap-2 hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ouvrir l'environnement externe: {session.external_url}
                    </a>
                  </div>
                )}

                {/* Terminal Body */}
                <div 
                  ref={terminalRef}
                  className="flex-1 p-4 overflow-y-auto font-mono text-sm"
                  style={{ background: "#0a0a0a" }}
                >
                  {history.map((item, index) => (
                    <div 
                      key={index}
                      className={`mb-1 ${
                        item.type === "input" 
                          ? "text-cyan-400" 
                          : item.type === "error" 
                          ? "text-red-400" 
                          : item.type === "system"
                          ? "text-yellow-400"
                          : "text-green-400"
                      }`}
                    >
                      <pre className="whitespace-pre-wrap">{item.content}</pre>
                    </div>
                  ))}
                  
                  {/* Input Line */}
                  <div className="flex items-center text-cyan-400">
                    <span className="text-gray-500">$</span>
                    <input
                      ref={inputRef}
                      type="text"
                      value={currentInput}
                      onChange={(e) => setCurrentInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="flex-1 ml-2 bg-transparent outline-none"
                      disabled={isProcessing}
                      autoFocus
                      data-testid="lab-terminal-input"
                    />
                    {isProcessing ? (
                      <span className="text-yellow-400 animate-pulse">...</span>
                    ) : (
                      <span className="w-2 h-4 bg-green-400 animate-pulse ml-0.5"></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* End Session Dialog */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terminer la session?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez complété {completedExercises.length}/{exercises.length} exercices.
              Votre score actuel est de {score} points.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuer</AlertDialogCancel>
            <AlertDialogAction onClick={endSession}>
              Terminer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-6 h-6 text-yellow-400" />
              Résultats du Lab
            </DialogTitle>
          </DialogHeader>
          
          {results && (
            <div className="py-4 space-y-4">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-4">
                  <Trophy className="w-10 h-10 text-yellow-400" />
                </div>
                <p className="text-4xl font-bold text-primary">
                  {results.score || 0}/{results.max_score || results.maxScore || 0}
                </p>
                <p className="text-muted-foreground">points obtenus</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Exercices complétés</span>
                  <span className="font-bold">
                    {results.completed_exercises || results.completed || completedExercises.length}/
                    {results.total_exercises || results.total || exercises.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Pourcentage</span>
                  <span className="font-bold">{results.percentage || Math.round(progress)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Statut</span>
                  <Badge className={results.passed || progress >= 70 
                    ? "bg-green-500/20 text-green-400" 
                    : "bg-red-500/20 text-red-400"
                  }>
                    {results.passed || progress >= 70 ? "Réussi" : "À améliorer"}
                  </Badge>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowResults(false);
                    setSession(null);
                  }}
                >
                  Nouveau Lab
                </Button>
                <Link to="/dashboard" className="flex-1">
                  <Button className="w-full">
                    Retour au Dashboard
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default InteractiveLab;
